// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI (multi-globe plan, phase 5, Science On a Sphere route A): the main
 * globe as one 2:1 equirectangular picture, the content a Science On a
 * Sphere computer warps onto its sphere.
 *
 * The picture is not a screenshot of the globe: the Earth layer draws the
 * same basemap, dataset and overlays once more with its sphere unwrapped
 * (`EarthTileLayerControl.requestUnwrap`), so every colour, palette and
 * border is the globe's own, at the feed's size, the whole Earth at once.
 * Pins are drawn over it here. The page that shows it is an output window
 * (`?output=…&sphere=2048`), which follows the presenter's window as any
 * output window does; the site captures that window and plays it.
 *
 * Projection: equirectangular (plate carrée), −180° at the left edge,
 * north on the top row: what SOS takes. Not Mercator (owner rule).
 */

/** A feed's size: 2048×1024 (the usual SOS dataset), or 4096×2048 when asked for. */
export function sphereFeedSize(param: string | null): { width: number; height: number } {
  const width = param === '4096' ? 4096 : 2048
  return { width, height: width / 2 }
}

/** A place in the picture (pixels, top left origin). */
export function equirectPoint(lat: number, lon: number, width: number, height: number): { x: number; y: number } {
  const wrapped = ((((lon + 180) % 360) + 360) % 360) - 180
  return {
    x: ((wrapped + 180) / 360) * width,
    y: ((90 - Math.max(-90, Math.min(90, lat))) / 180) * height,
  }
}

/** GL rows come bottom first; a canvas wants the top row first. */
export function flipRows(src: Uint8Array, width: number, height: number, out: Uint8ClampedArray): void {
  const row = width * 4
  for (let y = 0; y < height; y++) out.set(src.subarray((height - 1 - y) * row, (height - y) * row), y * row)
}

export interface SphereFeedSource {
  /** The main globe unwrapped, GL rows (bottom first); null while there is nothing to draw. */
  unwrap(width: number, height: number): Promise<Uint8Array | null>
  /** The pins on the globe. */
  pins(): ReadonlyArray<{ lat: number; lon: number; color: string }>
}

/** Draw pins as rings; one on the seam is drawn on both edges. */
export function drawFeedPins(ctx: CanvasRenderingContext2D, pins: ReadonlyArray<{ lat: number; lon: number; color: string }>, width: number, height: number): void {
  const r = height / 120
  for (const pin of pins) {
    const { x, y } = equirectPoint(pin.lat, pin.lon, width, height)
    for (const dx of x < r ? [0, width] : x > width - r ? [0, -width] : [0]) {
      ctx.beginPath()
      ctx.arc(x + dx, y, r, 0, Math.PI * 2)
      ctx.lineWidth = r / 2.5
      ctx.strokeStyle = '#000000'
      ctx.stroke()
      ctx.lineWidth = r / 4
      ctx.strokeStyle = pin.color
      ctx.stroke()
    }
  }
}

/**
 * Keep `canvas` showing the feed, a frame each time the last one is drawn.
 * Returns how to stop it.
 */
export function startSphereFeed(canvas: HTMLCanvasElement, source: SphereFeedSource, size: { width: number; height: number }): { stop(): void; frames(): number } {
  const { width, height } = size
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  let running = !!ctx
  let drawn = 0
  const image = ctx ? ctx.createImageData(width, height) : null
  const tick = async (): Promise<void> => {
    if (!running || !ctx || !image) return
    const rows = await source.unwrap(width, height).catch(() => null)
    if (!running) return
    if (rows && rows.length === width * height * 4) {
      flipRows(rows, width, height, image.data)
      ctx.putImageData(image, 0, 0)
      drawFeedPins(ctx, source.pins(), width, height)
      drawn++
    }
    requestAnimationFrame(() => { void tick() })
  }
  void tick()
  return {
    stop: () => { running = false },
    frames: () => drawn,
  }
}

export type FeedPin = { lat: number; lon: number; color: string }

/** The channel the pins travel on, beside the output window's own. */
export const feedPinsChannel = (channel: string): string => `${channel}-pins`

type Channel = Pick<BroadcastChannel, 'postMessage' | 'close'> & { onmessage: ((ev: MessageEvent) => void) | null }

/**
 * The window with the controls sends its pins to its output windows (the
 * room sync does not carry the Data tab's pins): on every change, and to
 * an output that asks when it opens. Returns how to stop.
 */
export function shareFeedPins(
  open: (name: string) => Channel,
  channel: string,
  list: () => readonly FeedPin[],
  subscribe: (fn: () => void) => () => void,
): () => void {
  const bc = open(feedPinsChannel(channel))
  const send = (): void => bc.postMessage({ t: 'pins', pins: list().map((p) => ({ lat: p.lat, lon: p.lon, color: p.color })) })
  bc.onmessage = (ev) => { if ((ev.data as { t?: string } | null)?.t === 'want') send() }
  const off = subscribe(send)
  send()
  return () => { off(); bc.close() }
}

/** An output window takes the pins its controlling window sends. */
export function receiveFeedPins(open: (name: string) => Channel, channel: string, onPins: (pins: FeedPin[]) => void): () => void {
  const bc = open(feedPinsChannel(channel))
  bc.onmessage = (ev) => {
    const data = ev.data as { t?: string; pins?: unknown } | null
    if (data?.t !== 'pins' || !Array.isArray(data.pins)) return
    onPins(data.pins.filter((p): p is FeedPin =>
      !!p && typeof p === 'object' && Number.isFinite((p as FeedPin).lat) && Number.isFinite((p as FeedPin).lon) && typeof (p as FeedPin).color === 'string' && /^#[0-9a-fA-F]{3,8}$/.test((p as FeedPin).color)).slice(0, 12))
  }
  bc.postMessage({ t: 'want' })
  return () => bc.close()
}
